import type { Appointment } from "src/app/core/models/admissions.model";
import { resolveEncounterClaimStatus } from "src/app/core/helpers/encounter-claim-status.util";
import { normalizeBillerClaimStatusDisplay } from "src/app/pages/biller/biller-claim-status.util";

/** Legacy PHI directory `eligibility` filter tokens (`true` / `false` / unset). */
const ELIGIBILITY_UNSET_FILTER_TOKENS = new Set(["null", "unknown", "none"]);

/**
 * Normalizes PHI directory `eligibility` — API returns labels like `Verified`, `Unverified`, `No Coverage`;
 * legacy rows may still carry boolean or `true`/`false` strings.
 */
export function normalizeWorksheetDirectoryEligibility(
  raw: unknown
): string | null {
  if (raw === true) {
    return "Verified";
  }
  if (raw === false) {
    return "Unverified";
  }
  if (raw == null) {
    return null;
  }
  const s = String(raw).trim();
  if (!s) {
    return null;
  }
  const lower = s.toLowerCase();
  if (lower === "true" || lower === "yes" || lower === "eligible") {
    return "Verified";
  }
  if (lower === "false" || lower === "no" || lower === "not eligible") {
    return "Unverified";
  }
  if (ELIGIBILITY_UNSET_FILTER_TOKENS.has(lower)) {
    return null;
  }
  return s;
}

/** @deprecated Use {@link normalizeWorksheetDirectoryEligibility}. */
export function parseWorksheetDirectoryEligibility(
  raw: unknown
): boolean | null | undefined {
  const normalized = normalizeWorksheetDirectoryEligibility(raw);
  if (normalized == null) {
    return null;
  }
  const lower = normalized.toLowerCase();
  if (lower === "verified" || lower === "eligible") {
    return true;
  }
  if (lower === "unverified" || lower === "not eligible") {
    return false;
  }
  return null;
}

export function formatWorksheetEligibilityDisplay(
  eligibility: string | boolean | null | undefined
): string {
  const normalized = normalizeWorksheetDirectoryEligibility(eligibility);
  return normalized ?? "--";
}

/** Eligibility column pill colors. */
export function getWorksheetEligibilityBadgeNgClass(
  appt: Appointment
): Record<string, boolean> {
  const label = formatWorksheetEligibilityDisplay(appt.eligibility);
  if (label === "--") {
    return {};
  }
  const n = label
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[\s_-]+/g, "");
  if (n === "verified" || n === "eligible") {
    return { "badge-soft-success": true };
  }
  if (n === "unverified" || n === "noteligible") {
    return { "badge-soft-warning": true };
  }
  if (n === "nocoverage" || n.includes("nocov")) {
    return { "badge-soft-danger": true };
  }
  return { "badge-soft-info": true };
}

/** Authorization column pill colors. */
export function getWorksheetAuthorizationBadgeNgClass(
  appt: Appointment
): Record<string, boolean> {
  const label = formatWorksheetAuthorizationDisplay(appt.authorizationStatus);
  if (label === "--") {
    return {};
  }
  const n = label
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[\s_-]+/g, "");
  if (
    (n.includes("authorized") || n === "auth") &&
    !n.includes("not") &&
    !n.includes("un") &&
    !n.includes("denied")
  ) {
    return { "badge-soft-success": true };
  }
  if (n.includes("denied") || n.includes("notauthorized") || n.includes("notauth")) {
    return { "badge-soft-danger": true };
  }
  if (n.includes("pending") || n.includes("required") || n.includes("review")) {
    return { "badge-soft-warning": true };
  }
  return { "badge-soft-info": true };
}

/** Claim Status column pill colors (aligned with biller claim status semantics). */
export function getWorksheetClaimStatusBadgeNgClass(
  appt: Appointment | null | undefined
): Record<string, boolean> {
  if (!appt) {
    return {};
  }
  const label = formatWorksheetClaimStatusDisplay(appt.claimStatus);
  if (label === "--") {
    return {};
  }
  const n = label
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[\s_-]+/g, "");
  if (
    n === "paid" ||
    n === "accepted" ||
    n.includes("partiallypaid") ||
    n.includes("partialpaid")
  ) {
    return { "badge-soft-success": true };
  }
  if (n.includes("denial") || n === "denied") {
    return { "badge-soft-danger": true };
  }
  if (
    n === "unpaid" ||
    n.includes("submitted") ||
    n.includes("claimprep") ||
    n.includes("casereview")
  ) {
    return { "badge-soft-warning": true };
  }
  if (n === "draft" || n === "sent" || n.includes("redacted")) {
    return { "badge-soft-secondary": true };
  }
  return { "badge-soft-info": true };
}

export function worksheetEligibilityFilterTokenLabel(token: string): string {
  const normalized = normalizeWorksheetDirectoryEligibility(token);
  return normalized ?? (token.trim() || token);
}

export function worksheetEligibilityFilterTokenMatchesRow(
  filterToken: string,
  eligibility: string | boolean | null | undefined
): boolean {
  const t = filterToken.trim().toLowerCase();
  if (!t) {
    return false;
  }
  const rowLabel = formatWorksheetEligibilityDisplay(eligibility);
  if (rowLabel === "--") {
    return ELIGIBILITY_UNSET_FILTER_TOKENS.has(t);
  }
  if (rowLabel.trim().toLowerCase() === t) {
    return true;
  }
  const rowNormalized = normalizeWorksheetDirectoryEligibility(eligibility);
  const tokenNormalized = normalizeWorksheetDirectoryEligibility(filterToken);
  if (
    rowNormalized &&
    tokenNormalized &&
    rowNormalized.toLowerCase() === tokenNormalized.toLowerCase()
  ) {
    return true;
  }
  if (t === "true" && rowNormalized?.toLowerCase() === "verified") {
    return true;
  }
  if (t === "false" && rowNormalized?.toLowerCase() === "unverified") {
    return true;
  }
  return false;
}

export function formatWorksheetAuthorizationDisplay(
  status: string | null | undefined
): string {
  const s = (status ?? "").trim();
  return s || "--";
}

export function formatWorksheetClaimStatusDisplay(
  status: string | null | undefined
): string {
  const s = (status ?? "").trim();
  if (!s) {
    return "--";
  }
  return normalizeBillerClaimStatusDisplay(s);
}

export function getWorksheetPrimaryPayerDisplay(appt: Appointment): string {
  const name = (appt.primaryPayerName ?? "").trim();
  return name || "--";
}

export function getWorksheetEligibilityColumnDisplay(appt: Appointment): string {
  return formatWorksheetEligibilityDisplay(appt.eligibility);
}

export function getWorksheetEligibilityColumnBadgeNgClass(
  appt: Appointment
): Record<string, boolean> {
  return getWorksheetEligibilityBadgeNgClass(appt);
}

export function getWorksheetAuthorizationColumnDisplay(appt: Appointment): string {
  return formatWorksheetAuthorizationDisplay(appt.authorizationStatus);
}

export function getWorksheetAuthorizationColumnBadgeNgClass(
  appt: Appointment
): Record<string, boolean> {
  return getWorksheetAuthorizationBadgeNgClass(appt);
}

export function getWorksheetClaimStatusColumnDisplay(
  appt: Appointment | null | undefined
): string {
  if (!appt) {
    return formatWorksheetClaimStatusDisplay(undefined);
  }
  const resolved = resolveEncounterClaimStatus(appt);
  const raw =
    resolved !== undefined ? resolved : (appt.claimStatus ?? "").trim();
  return formatWorksheetClaimStatusDisplay(raw);
}

export function getWorksheetClaimStatusColumnBadgeNgClass(
  appt: Appointment | null | undefined
): Record<string, boolean> {
  if (!appt) {
    return getWorksheetClaimStatusBadgeNgClass(undefined);
  }
  const resolved = resolveEncounterClaimStatus(appt);
  const raw =
    resolved !== undefined ? resolved : (appt.claimStatus ?? "").trim();
  return getWorksheetClaimStatusBadgeNgClass({ ...appt, claimStatus: raw });
}

export function collectWorksheetBillerFacetOptionValues(
  column: "payer" | "eligibility" | "auth" | "claimStatus",
  rows: readonly Appointment[]
): { label: string; value: string }[] {
  const seen = new Map<string, string>();
  for (const appt of rows) {
    let raw = "";
    let label = "";
    switch (column) {
      case "payer":
        raw = (appt.primaryPayerName ?? "").trim();
        label = raw || "--";
        break;
      case "eligibility":
        raw = formatWorksheetEligibilityDisplay(appt.eligibility);
        label = raw;
        break;
      case "auth":
        raw = (appt.authorizationStatus ?? "").trim();
        label = formatWorksheetAuthorizationDisplay(raw);
        break;
      case "claimStatus":
        raw = (appt.claimStatus ?? "").trim();
        label = formatWorksheetClaimStatusDisplay(raw);
        break;
    }
    if (!raw || raw === "--") {
      raw = "--";
    }
    const key = raw.toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, label);
    }
  }
  return [...seen.entries()]
    .map(([value, lbl]) => ({ value, label: lbl }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
}
